import { useMemo } from "react";
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { VenueCarouselItem } from "@punchup/data/models";
import SortableCarouselItem from "./SortableCarouselItem";
import PendingImageCarouselItem from "./PendingImageCarouselItem";
import { PendingUpload } from "./types";

interface VenueBannerCarouselItemsProps {
  isLoading: boolean;
  uploadedLabel: string | null;
  items: VenueCarouselItem[] | undefined;
  pendingUploads: PendingUpload[];
  previewUrls: Map<string, string>;
  onOrderChange: (reorderedItems: VenueCarouselItem[]) => void;
  isReordering: boolean;
  onDelete?: (item: VenueCarouselItem) => void;
  venuePageId: string;
}

export default function VenueBannerCarouselItems({
  isLoading,
  uploadedLabel,
  items,
  pendingUploads,
  previewUrls,
  onOrderChange,
  isReordering,
  onDelete,
  venuePageId,
}: VenueBannerCarouselItemsProps) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Sort items once and reuse throughout the component
  const sortedItems = useMemo(
    () => items?.slice().sort((a, b) => a.order - b.order) ?? [],
    [items]
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !items) return;

    const oldIndex = sortedItems.findIndex((item) => item.id === active.id);
    const newIndex = sortedItems.findIndex((item) => item.id === over.id);
    const reorderedItems = arrayMove(sortedItems, oldIndex, newIndex);

    onOrderChange(reorderedItems);
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-start gap-y-3 w-full">
        {/* the label */}
        <div className="h-[20px] w-[200px] rounded-lg bg-gray-200 animate-pulse" />
        {/* the carousel grid items */}
        <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-4 w-full">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="aspect-[16/9] rounded-lg bg-gray-200 animate-pulse"
            />
          ))}
        </div>
      </div>
    );
  }

  const hasItems = sortedItems.length > 0 || pendingUploads.length > 0;

  if (!hasItems) {
    return null;
  }

  return (
    <div className="flex flex-col gap-y-3">
      {uploadedLabel && (
        <p className="text-sm text-secondary-blue">{uploadedLabel}</p>
      )}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={sortedItems.map((item) => item.id)}
          strategy={rectSortingStrategy}
        >
          <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-4">
            {sortedItems.map((item) => (
              <SortableCarouselItem
                key={item.id}
                item={item}
                previewUrls={previewUrls}
                isDragDisabled={isReordering}
                onDelete={onDelete}
                venuePageId={venuePageId}
              />
            ))}
            {pendingUploads.map((pending) => (
              <PendingImageCarouselItem
                key={pending.id}
                previewUrl={pending.previewUrl}
                isSaving={!!pending.cloudflareImageId}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}
